import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import PropTypes from 'prop-types';
import React from 'react';
import Button from './Button.js';
import Icon from './Icon.js';
import { a as addClassNames, c as classNameProps } from './classNames-6a4763fd.js';
import { a as addSpacingClasses, m as marginProps, p as paddingProps } from './spacing-d13ff368.js';
import { p as props } from './componentEl-33ff2aeb.js';

function findBounds(index, menuSize, totalPages) {
  const leftSide = Math.floor((menuSize - 1) / 2);
  const rightSide = Math.ceil((menuSize - 1) / 2);

  if (index - leftSide < 1) {
    const end = totalPages > menuSize ? menuSize : totalPages;
    return [1, end];
  }

  if (index + rightSide > totalPages) {
    const start = totalPages - menuSize + 1 > 0 ? totalPages - menuSize + 1 : 1;
    return [start, totalPages];
  }

  return [index - leftSide, index + rightSide];
}
function mapButtons(Component, index, menuSize, _onClick, otherProps, passAttributeToMenuItem, totalPages) {
  const [start, end] = findBounds(index, menuSize, totalPages);
  const numberButtons = [];

  for (let i = start; i <= end; i += 1) {
    const isActive = index === i;
    numberButtons.push( /*#__PURE__*/React.createElement(Button, _extends({
      componentEl: Component,
      disabled: isActive,
      key: i,
      margin: {
        left: 'xs',
        right: 'xs'
      },
      onClick: e => _onClick(e, i),
      tertiary: true,
      type: "button"
    }, otherProps, passAttributeToMenuItem(i)), i));
  }

  return numberButtons;
}

/**
 * Tests a prop of type string contains a valid number.
 */
function positiveNonZeroInt(props, propName, componentName) {
  const errorMessage = ["Invalid prop " + propName + " supplied to " + componentName + "."];
  const {
    [propName]: prop
  } = props;

  if (typeof prop !== 'number') {
    errorMessage.push("Prop is of type " + typeof prop + ".");
  }

  if (prop < 1) {
    errorMessage.push('Prop is less than 1.');
  }

  if (!Number.isInteger(prop)) {
    errorMessage.push('Prop is a float.');
  }

  errorMessage.push('Expected a positive, non-zero integer.');
  if (errorMessage.length > 2) return new Error(errorMessage.join(' '));
  return null;
}

function positiveNonZeroIntPropType(props, propName, componentName) {
  if (props[propName] === undefined || props[propName] === null) return null;
  return positiveNonZeroInt(props, propName, componentName);
}

positiveNonZeroIntPropType.isRequired = function isRequired(props, propName, componentName) {
  return positiveNonZeroIntPropType(props, propName, componentName);
};

function Pagination(_ref) {
  let {
    onClick: _onClick,
    index: index$1,
    menuSize,
    passAttributeToMenuItem,
    showEndOfMenu,
    showPrevAndNext,
    totalPages,
    className,
    componentEl: Component,
    margin,
    padding
  } = _ref,
      otherProps = _objectWithoutPropertiesLoose(_ref, ["onClick", "index", "menuSize", "passAttributeToMenuItem", "showEndOfMenu", "showPrevAndNext", "totalPages", "className", "componentEl", "margin", "padding"]);

  let cssClasses = ['st_pagination'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  return /*#__PURE__*/React.createElement("div", {
    className: cssClasses.join(' '),
    "aria-label": "Page navigation"
  }, showPrevAndNext && totalPages > menuSize && /*#__PURE__*/React.createElement(Button, _extends({
    className: "st_pagination__first-button",
    componentEl: Component,
    disabled: index$1 === 1,
    onClick: e => _onClick(e, 1),
    tertiary: true,
    type: "button",
    "aria-label": "start"
  }, otherProps, passAttributeToMenuItem(1)), /*#__PURE__*/React.createElement(Icon, {
    type: "caret-left",
    className: "st_pagination__icon-first"
  }), /*#__PURE__*/React.createElement(Icon, {
    type: "caret-left",
    className: "st_pagination__icon-second"
  })), showPrevAndNext && /*#__PURE__*/React.createElement(Button, _extends({
    className: "st_pagination__prev-button",
    componentEl: Component,
    disabled: index$1 === 1,
    iconPosition: "left",
    onClick: e => _onClick(e, index$1 - 1),
    tertiary: true,
    type: "button",
    "aria-label": "previous"
  }, otherProps, passAttributeToMenuItem(index$1 - 1)), /*#__PURE__*/React.createElement(Icon, {
    size: "mlg",
    type: "caret-left"
  })), mapButtons(Component, index$1, menuSize, _onClick, otherProps, passAttributeToMenuItem, totalPages), showEndOfMenu && index$1 + Math.ceil((menuSize + 1) / 2) <= totalPages && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "st_pagination__ellipsis"
  }, "..."), /*#__PURE__*/React.createElement(Button, _extends({
    componentEl: Component,
    disabled: index$1 === totalPages,
    margin: {
      left: 'xs',
      right: 'xs'
    },
    onClick: e => _onClick(e, totalPages),
    tertiary: true,
    type: "button"
  }, otherProps, passAttributeToMenuItem(totalPages)), totalPages)), showPrevAndNext && /*#__PURE__*/React.createElement(Button, _extends({
    className: "st_pagination__next-button",
    componentEl: Component,
    disabled: index$1 === totalPages,
    iconPosition: "right",
    onClick: e => _onClick(e, index$1 + 1),
    tertiary: true,
    type: "button",
    "aria-label": "next"
  }, otherProps, passAttributeToMenuItem(index$1 + 1)), /*#__PURE__*/React.createElement(Icon, {
    size: "mlg",
    type: "caret-right"
  })), showPrevAndNext && totalPages > menuSize && /*#__PURE__*/React.createElement(Button, _extends({
    className: "st_pagination__last-button",
    componentEl: Component,
    disabled: index$1 === totalPages,
    onClick: e => _onClick(e, totalPages),
    tertiary: true,
    type: "button",
    "aria-label": "end"
  }, otherProps, passAttributeToMenuItem(totalPages)), /*#__PURE__*/React.createElement(Icon, {
    type: "caret-right",
    className: "st_pagination__icon-first"
  }), /*#__PURE__*/React.createElement(Icon, {
    type: "caret-right",
    className: "st_pagination__icon-second"
  })));
}

Pagination.propTypes = {
  /** a function to handle click events in the menu, takes in the click event and index */
  onClick: PropTypes.func,

  /** current index, required */
  index: positiveNonZeroIntPropType.isRequired,

  /** number of items to be displayed in the menu, defaults to 5 */
  menuSize: PropTypes.number,

  /**
   * a function passed to each item in the menu, takes in the index value of that item, and
   * returns an object comprised of any key value pairs that need to be passed as props to each
   * item in the menu (e.g. i => ({ to: `/?page=${i}` }), if a react-router `Link` tag is used for
   * each item), optional
   */
  passAttributeToMenuItem: PropTypes.func,

  /**
   * a flag that indicates whether or not to show the last item in the menu if applicable, defaults
   * to false
   */
  showEndOfMenu: PropTypes.bool,

  /**
   * a flag that indicates whether or not to show the "Prev" and "Next" buttons and "First" and
   * "Last" buttons if `totalPages` is larger than `menuSize`, defaults to true
   */
  showPrevAndNext: PropTypes.bool,

  /** the total number of pages available, required */
  totalPages: positiveNonZeroIntPropType.isRequired,

  /** extra CSS classes, if any */
  className: classNameProps,

  /** dom element or component to use for the button */
  componentEl: props,

  /** margin for component, if any */
  margin: marginProps,

  /** padding for component, if any */
  padding: paddingProps
};
Pagination.defaultProps = {
  onClick: () => {},
  menuSize: 5,
  passAttributeToMenuItem: () => {},
  showEndOfMenu: false,
  showPrevAndNext: true,
  className: null,
  componentEl: Button,
  margin: null,
  padding: null
};

export default Pagination;
